(() => {
  if (customElements.get('aimance-video-stories')) return;

  const instances = new Set();
  const scrollOwners = new Set();
  let activeInstance = null;
  let scrollSnapshot = null;
  let titleSequence = 0;

  const refreshPreviews = () => instances.forEach((instance) => instance.syncPreviews());

  const lockScroll = (owner) => {
    if (scrollOwners.has(owner)) return;
    if (!scrollOwners.size) {
      const body = document.body;
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      const applied = {
        position: 'fixed',
        top: `${-window.scrollY}px`,
        left: '0px',
        right: '0px',
        width: '100%',
        overflow: 'hidden',
        'box-sizing': 'border-box',
        'padding-right': `${padding + scrollbar}px`
      };
      const previous = Object.fromEntries(Object.keys(applied).map((name) => [name, {
        value: body.style.getPropertyValue(name),
        priority: body.style.getPropertyPriority(name)
      }]));
      scrollSnapshot = { body, previous, applied, x: window.scrollX, y: window.scrollY };
      Object.entries(applied).forEach(([name, value]) => body.style.setProperty(name, value, 'important'));
    }
    scrollOwners.add(owner);
  };

  const unlockScroll = (owner) => {
    if (!scrollOwners.delete(owner) || scrollOwners.size || !scrollSnapshot) return;
    const { body, previous, applied, x, y } = scrollSnapshot;
    scrollSnapshot = null;
    const restorePosition = body.style.getPropertyValue('position') === applied.position
      && body.style.getPropertyPriority('position') === 'important';
    Object.entries(previous).forEach(([name, saved]) => {
      if (body.style.getPropertyValue(name) !== applied[name]
        || body.style.getPropertyPriority(name) !== 'important') return;
      if (saved.value) body.style.setProperty(name, saved.value, saved.priority);
      else body.style.removeProperty(name);
    });
    if (restorePosition) {
      const root = document.documentElement;
      const behavior = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(x, y);
      if (behavior) root.style.setProperty('scroll-behavior', behavior, priority);
      else root.style.removeProperty('scroll-behavior');
    }
  };

  class AimanceVideoStories extends HTMLElement {
    static get observedAttributes() {
      return ['data-autoplay'];
    }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const own = (element) => element.closest('aimance-video-stories') === this;
      this.cards = Array.from(this.querySelectorAll('[data-story-open]')).filter(own);
      this.previews = Array.from(this.querySelectorAll('[data-story-preview]')).filter(own);
      this.dialog = this.querySelector('.avs__dialog');
      this.player = this.dialog?.querySelector('[data-story-player]');
      this.closeButton = this.dialog?.querySelector('[data-story-close]');
      this.position = this.dialog?.querySelector('[data-story-position]');
      this.dialogTitle = this.dialog?.querySelector('.avs__dialog-title');
      this.previewState = new Map();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motionChange = () => this.syncPreviews();
      this.playRequest = 0;
      this.currentIndex = -1;
      this.opened = false;
      instances.add(this);

      if (this.motion.addEventListener) this.motion.addEventListener('change', this.motionChange);
      else this.motion.addListener(this.motionChange);

      this.previews.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.controls = false;
        video.preload = 'none';
        video.tabIndex = -1;
        video.setAttribute('aria-hidden', 'true');
        this.previewState.set(video, { visible: false, pending: false, revision: 0 });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.previewState.get(entry.target);
            if (!state) return;
            state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
            this.syncPreview(entry.target, state);
          });
        }, { threshold: [0, 0.35] });
        this.previews.forEach((video) => this.observer.observe(video));
      }

      this.addEventListener('click', (event) => {
        const card = event.target.closest?.('[data-story-open]');
        if (!card || !own(card)) return;
        event.preventDefault();
        this.open(this.cards.indexOf(card), card);
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.player?.pause();
        this.syncPreviews();
      }, { signal });

      window.addEventListener('pagehide', () => {
        this.previews.forEach((video) => video.pause());
        this.player?.pause();
      }, { signal });

      window.addEventListener('pageshow', () => this.syncPreviews(), { signal });

      if (!this.dialog || !this.player) return;
      this.player.controls = true;
      this.player.playsInline = true;
      this.player.preload = 'none';
      ['start', 'end'].forEach((edge) => {
        let guard = this.dialog.querySelector(`[data-story-focus-guard="${edge}"]`);
        if (!guard) {
          guard = document.createElement('span');
          guard.className = 'avs__focus-guard';
          guard.dataset.storyFocusGuard = edge;
          guard.tabIndex = 0;
          if (edge === 'start') this.dialog.prepend(guard);
          else this.dialog.append(guard);
        }
        guard.addEventListener('focus', () => this.focusBoundary(edge === 'start'), { signal });
      });
      if (this.dialogTitle && !this.dialog.hasAttribute('aria-labelledby')) {
        if (!this.dialogTitle.id) this.dialogTitle.id = `avs-dialog-title-${++titleSequence}`;
        this.dialog.setAttribute('aria-labelledby', this.dialogTitle.id);
      }
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.dialog.querySelector('[data-story-prev]')?.addEventListener('click', () => this.navigate(-1), { signal });
      this.dialog.querySelector('[data-story-next]')?.addEventListener('click', () => this.navigate(1), { signal });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.finishClose();
      }, { signal });
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPointer = this.outsideDialog(event);
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.backdropPointer && this.outsideDialog(event)) this.close();
        this.backdropPointer = false;
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
          || event.target.closest?.('video, input, textarea, select, [contenteditable="true"]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.navigate(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
    }

    attributeChangedCallback() {
      if (this.controller) this.syncPreviews();
    }

    disconnectedCallback() {
      this.close(false);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.observer = null;
      if (this.motion && this.motionChange) {
        if (this.motion.removeEventListener) this.motion.removeEventListener('change', this.motionChange);
        else this.motion.removeListener(this.motionChange);
      }
      this.previews?.forEach((video) => {
        video.pause();
        video.querySelectorAll('source[data-src]').forEach((source) => source.removeAttribute('src'));
        video.load();
      });
      this.previewState?.clear();
      instances.delete(this);
      refreshPreviews();
    }

    canPreview() {
      return this.isConnected && this.dataset.autoplay === 'true'
        && !this.motion?.matches && !document.hidden && !activeInstance;
    }

    syncPreview(video, state) {
      state.revision += 1;
      if (!state.visible || !this.canPreview()) {
        video.pause();
        return;
      }
      let loaded = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (source.hasAttribute('src')) return;
        source.src = source.dataset.src;
        loaded = true;
      });
      if (loaded) video.load();
      if (!video.paused || state.pending) return;
      state.pending = true;
      const revision = state.revision;
      const playing = video.play();
      Promise.resolve(playing).then(() => {
        if (this.previewState.get(video) !== state) return;
        if (!state.visible || !this.canPreview()) video.pause();
      }).catch(() => {}).finally(() => {
        state.pending = false;
        if (this.previewState.get(video) === state && state.revision !== revision
          && state.visible && this.canPreview() && video.paused) this.syncPreview(video, state);
      });
    }

    syncPreviews() {
      this.previewState?.forEach((state, video) => this.syncPreview(video, state));
    }

    outsideDialog(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right
        || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    focusBoundary(last) {
      const controls = Array.from(this.dialog.querySelectorAll(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), video[controls], [tabindex]'
      )).filter((element) => !element.hasAttribute('data-story-focus-guard')
        && !element.closest('[hidden], [inert]')
        && (element.tabIndex >= 0 || element.matches('video[controls]:not([tabindex="-1"])'))
        && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      const target = last ? controls[controls.length - 1] : controls[0];
      target?.focus({ preventScroll: true });
    }

    open(index = 0, trigger = null) {
      if (!this.isConnected || !this.dialog || !this.player || !this.cards[index]
        || !this.cards[index].dataset.videoSrc || typeof this.dialog.showModal !== 'function') return;
      if (activeInstance && activeInstance !== this) activeInstance.close(false);
      if (!this.opened) {
        this.restoreFocusOnClose = true;
        this.trigger = trigger || document.activeElement;
        if (this.dialogTitle) {
          this.dialogTitle.textContent = this.cards[index].dataset.videoTitle
            || this.cards[index].getAttribute('aria-label') || '';
        }
        try {
          this.dialog.showModal();
        } catch {
          return;
        }
        this.opened = true;
        activeInstance = this;
        lockScroll(this);
        refreshPreviews();
        this.player.muted = false;
        this.closeButton?.focus({ preventScroll: true });
      }
      this.showStory(index);
    }

    showStory(index) {
      const card = this.cards[index];
      if (!card?.dataset.videoSrc || !this.opened) return;
      this.currentIndex = index;
      this.player.pause();
      this.player.src = card.dataset.videoSrc;
      if (card.dataset.videoPoster) this.player.poster = card.dataset.videoPoster;
      else this.player.removeAttribute('poster');
      if (this.dialogTitle) this.dialogTitle.textContent = card.dataset.videoTitle || card.getAttribute('aria-label') || '';
      if (this.position) this.position.textContent = `${index + 1} / ${this.cards.length}`;
      this.dialog.querySelectorAll('[data-story-prev], [data-story-next]').forEach((button) => {
        button.disabled = this.cards.length < 2;
      });
      this.player.load();
      const request = ++this.playRequest;
      if (!document.hidden) {
        Promise.resolve(this.player.play()).then(() => {
          if (request === this.playRequest && (!this.opened || document.hidden)) this.player.pause();
        }).catch(() => {});
      }
      this.dispatchEvent(new CustomEvent('avs:change', { bubbles: true, detail: { index } }));
    }

    navigate(direction) {
      if (!this.opened || this.cards.length < 2) return;
      this.showStory((this.currentIndex + direction + this.cards.length) % this.cards.length);
    }

    close(restoreFocus = true) {
      if (!this.dialog) return;
      this.restoreFocusOnClose = restoreFocus;
      if (this.dialog.open) this.dialog.close();
      this.finishClose();
    }

    finishClose() {
      if (!this.opened) return;
      this.opened = false;
      this.playRequest += 1;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.removeAttribute('poster');
      this.player.load();
      if (activeInstance === this) activeInstance = null;
      unlockScroll(this);
      if (this.restoreFocusOnClose !== false && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.restoreFocusOnClose = true;
      this.trigger = null;
      refreshPreviews();
    }
  }

  customElements.define('aimance-video-stories', AimanceVideoStories);
})();
